import React from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Panel, Empty } from './HeadlineCards';

const AXIS = { stroke: '#52525b', fontSize: 11, tickLine: false, axisLine: false };
const TIP = { contentStyle: { background: '#18181b', border: '1px solid #3f3f46', borderRadius: 12, fontSize: 12 }, labelStyle: { color: '#fafafa' }, cursor: { fill: 'rgba(255,255,255,0.04)' } };

export const ModuleCompletionChart = ({ modules, onSelect }) => (
  <Panel title="Completion by module" subtitle="Click a bar to see who hasn't finished" testId="insights-module-chart">
    {!modules.length ? <Empty>No modules yet.</Empty> : (
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={modules} margin={{ left: -20, right: 8 }}>
          <CartesianGrid vertical={false} stroke="#27272a" />
          <XAxis dataKey="title" {...AXIS} interval={0} tickFormatter={(t) => (t?.length > 12 ? `${t.slice(0, 11)}…` : t)} />
          <YAxis domain={[0, 100]} unit="%" {...AXIS} />
          <Tooltip {...TIP} formatter={(v, k, p) => [`${v}% · ${p.payload.not_finished} not finished`, 'Completion']} />
          <Bar dataKey="completion_pct" radius={[6, 6, 0, 0]} cursor="pointer" onClick={(d) => onSelect(d.payload || d)}>
            {modules.map((m) => <Cell key={m.module_id} fill={m.completion_pct >= 100 ? '#D4AF37' : '#14b8a6'} data-testid={`insights-module-bar-${m.module_id}`} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    )}
    <div className="flex flex-wrap gap-2 mt-3">
      {modules.map((m) => (
        <button key={m.module_id} type="button" onClick={() => onSelect(m)} data-testid={`insights-module-drill-${m.module_id}`}
          className="text-[11px] px-2.5 py-1 rounded-full border border-zinc-800 text-zinc-400 hover:border-teal-500/50 hover:text-teal-300 transition-colors">
          {m.title} · {m.not_finished} pending
        </button>
      ))}
    </div>
  </Panel>
);

export const WeeklyActiveChart = ({ weeks, total }) => (
  <Panel title="Weekly active learners" subtitle={`Last 8 weeks · ${total} enrolled`} testId="insights-weekly-chart">
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={weeks} margin={{ left: -20, right: 8 }}>
        <CartesianGrid vertical={false} stroke="#27272a" />
        <XAxis dataKey="week_start" {...AXIS} tickFormatter={(d) => new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} />
        <YAxis allowDecimals={false} {...AXIS} />
        <Tooltip {...TIP} labelFormatter={(d) => `Week of ${new Date(d).toLocaleDateString()}`} formatter={(v) => [v, 'Active learners']} />
        <Line type="monotone" dataKey="active" stroke="#D4AF37" strokeWidth={2.5} dot={{ r: 3, fill: '#D4AF37' }} activeDot={{ r: 5 }} />
      </LineChart>
    </ResponsiveContainer>
  </Panel>
);

export const AssignmentsChart = ({ assignments, onSelect }) => (
  <Panel title="Assignments" subtitle="Submitted vs pending vs overdue · click a bar to see who hasn't submitted" testId="insights-assignments-chart">
    {!assignments.length ? <Empty>No assignments in this programme yet.</Empty> : (
      <>
        <ResponsiveContainer width="100%" height={Math.max(160, assignments.length * 44)}>
          <BarChart data={assignments} layout="vertical" margin={{ left: 8, right: 16 }}>
            <CartesianGrid horizontal={false} stroke="#27272a" />
            <XAxis type="number" allowDecimals={false} {...AXIS} />
            <YAxis type="category" dataKey="title" width={170} {...AXIS} tickFormatter={(t) => (t?.length > 24 ? `${t.slice(0, 23)}…` : t)} />
            <Tooltip {...TIP} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="submitted" name="Submitted" stackId="a" fill="#14b8a6" cursor="pointer" onClick={(d) => onSelect(d.payload || d)} />
            <Bar dataKey="pending" name="Pending" stackId="a" fill="#71717a" cursor="pointer" onClick={(d) => onSelect(d.payload || d)} />
            <Bar dataKey="overdue" name="Overdue" stackId="a" fill="#f43f5e" radius={[0, 6, 6, 0]} cursor="pointer" onClick={(d) => onSelect(d.payload || d)} />
          </BarChart>
        </ResponsiveContainer>
        <div className="flex flex-wrap gap-2 mt-3">
          {assignments.map((a) => (
            <button key={a.activity_id} type="button" onClick={() => onSelect(a)} data-testid={`insights-assignment-drill-${a.activity_id}`}
              className="text-[11px] px-2.5 py-1 rounded-full border border-zinc-800 text-zinc-400 hover:border-rose-500/50 hover:text-rose-300 transition-colors">
              {a.title} · {a.pending + a.overdue} outstanding
            </button>
          ))}
        </div>
      </>
    )}
  </Panel>
);
